import React, { useRef } from "react";
import { useRootDispatch, useRootSelector } from "~redux/hooks";
import { setWindGridAltitude, viewSelector, windgridStateSelector, closeView } from "~redux/slices/edstSlice";
import { useDragging } from "hooks/useDragging";
import {
  colorNameMap,
  eramTextDimensionMap,
  MenuElement,
  useCenterCursor,
  useFocused,
  usePixiOnClickOutside,
} from "@poscon/shared-frontend";
import type { Container as PixiContainer } from "pixi.js";
import { useZIndex } from "hooks/useZIndex";
import { MenuTitleBar } from "components/utils/MenuTitleBar";
import { cyan, edstFontDim } from "~/utils/constants";
import { EdstButton } from "~/components/utils/EdstButton";
import { InteractiveContainer } from "~/components/utils/InteractiveContainer";

const { width: fontWidth, height: fontHeight } = eramTextDimensionMap[2];

const width = 16 * fontWidth;
const height = 31 * (fontHeight + 6) + 10;

const view = "WIND_ALT_MENU";

const itemWidth = fontWidth * 3 + 4;

export const WindAltMenu = () => {
  const dispatch = useRootDispatch();
  const viewProps = useRootSelector((state) => viewSelector(state, view));
  const windgridParams = useRootSelector(windgridStateSelector);
  const ref = useRef<PixiContainer>(null);
  const zIndex = useZIndex(view, ref);
  useCenterCursor(ref);
  const focused = useFocused(ref);
  const { startDrag } = useDragging(ref, view, "mouseup");

  usePixiOnClickOutside(ref, () => dispatch(closeView("WIND_ALT_MENU")));

  return (
    <InteractiveContainer ref={ref} x={viewProps.position.x} y={viewProps.position.y} zIndex={zIndex}>
      <MenuTitleBar
        title="Altitude Menu"
        width={width}
        onTitleMouseDown={startDrag}
        view={view}
        fill={focused ? cyan : colorNameMap.mediumGrey}
        tint={colorNameMap.black}
        withX={false}
      />
      <container y={fontHeight + 2} sortableChildren>
        <graphics
          draw={(graphics) => {
            graphics.clear();
            graphics.rect(0, 0, width, height).fill(0).stroke({ width: 1, color: colorNameMap.grey });
          }}
        />
        {Array.from({ length: 59 }, (_, i) => {
          const v = (i + 1) * 10;
          const selected = windgridParams.altitude === v;
          return (
            <MenuElement
              key={i}
              x={i < 30 ? 20 : width - itemWidth - 20}
              y={4 + (fontHeight + 6) * (i % 30)}
              text={v.toString().padStart(3, "0")}
              width={itemWidth}
              borderColor={0x000000}
              selected={selected}
              selectReverseVideo
              tint={colorNameMap.lightGrey}
              onmousedown={() => {
                dispatch(setWindGridAltitude(v));
              }}
            />
          );
        })}
        <EdstButton
          x={width - edstFontDim.width * 4 - 15}
          y={height - edstFontDim.height - 8}
          text="Exit"
          onmousedown={() => dispatch(closeView("WIND_ALT_MENU"))}
        />
      </container>
    </InteractiveContainer>
  );
};
